/**
 * PlaquetteProspects.jsx — Page publique teaser pour prospects (sans token)
 *
 * Accessible via le QR "PLAQUETTE 2026" de la carte de visite digitale ou
 * directement depuis /plaquette-prospects.
 *
 * Contenu :
 *   - Hero argumentaire (Zéro véhicule / Gain 5200€/mois)
 *   - 3 cartes formations AM/A1/A2 CLIQUABLES → modal détail + CTA Devis/Infos
 *     • Devis → crée un devis orphelin via /api/public/prestation-devis
 *               et l'envoie par email au prospect avec lien de signature
 *     • Infos → poste vers /api/public/contact
 *   - Passerelles 7h sans examen (teaser)
 *   - CTA "Demander l'accès partenaire" (mailto pré-rempli)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, Shield, Users, TrendingUp, Phone, Mail, MapPin, Clock,
  CheckCircle2, Sparkles, ArrowRight,
} from 'lucide-react';
import PrestationDialog from '../components/PrestationDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const VAULT_BASE = '/b2b-vault-PWdOvjJBlIfehjRw5j_Yjg';

// Normalise une formation vers la shape attendue par PrestationDialog
const toPrestation = (f) => ({
  code:               f.code,
  catalogCode:        f.code,
  title:              f.title,
  subtitle:           f.sub,
  badge:              f.badge,
  duration:           f.duration,
  price:              f.price,
  priceLabel:         f.priceLabel,
  priceNote:          f.priceNote,
  priceOfficialRange: f.priceOfficialRange,
  visual:             { kind: 'img', src: f.img },
  publicCible:        f.publicCible,
  keyPoints:          f.keyPoints,
  dispenses:          f.dispenses,
  notes:              f.notes,
  legalRef:           f.legalRef,
  accent:             'orange',
});

const FORMATIONS = [
  {
    code: 'am',
    img:  `${VAULT_BASE}/moto-ami-am.png`,
    badge:'AM Quadricycle',
    title:'Permis AM — Citroën Ami',
    sub:  'Dès 14 ans · 8h formation · Sans examen',
    priceLabel: 'à partir de',
    price: '420 €',
    priceNote: 'Prix public conseillé TTC',
    duration: '8h formation',
    publicCible:
      "Jeunes dès 14 ans souhaitant conduire un quadricycle léger (Citroën Ami, Aixam, Ligier…). Formation obligatoire sans examen final.",
    keyPoints: [
      'Véhicule fourni (Citroën Ami 100% électrique)',
      'Équipement complet inclus (casque, gants, gilet)',
      'Formation sur circuit fermé + voies ouvertes',
      'Attestation remise le jour même (BSR / permis AM)',
      'Rattachement sous votre agrément — vous gardez le client',
    ],
    legalRef: 'Arrêté du 8 janvier 2013 · Catégorie AM',
  },
  {
    code: 'a1',
    img:  `${VAULT_BASE}/moto-honda-cb125-a1.png?v=3`,
    badge:'A1 125 cm³',
    title:'Permis A1 — Honda CB125',
    sub:  'Dès 16 ans · 20h · Plateau + Circulation',
    priceLabel: 'à partir de',
    price: '1 190 €',
    priceNote: 'Prix public conseillé TTC',
    duration: '20h minimum',
    publicCible:
      "Candidats de 16 à 18 ans visant le permis moto A1 (125 cm³, ≤ 11 kW). Également ouvert à l'extension depuis le B.",
    keyPoints: [
      'Moto fournie (Honda CB125F ou CB125R)',
      '8h plateau homologué + 12h circulation réelle',
      'Intercom radio pour coaching en temps réel',
      'Préparation examen plateau + circulation ETM',
      'Vêtements protection fournis si besoin',
    ],
    legalRef: 'Arrêté 23 avril 2012 modifié · Catégorie A1',
  },
  {
    code: 'a2',
    img:  `${VAULT_BASE}/moto-mt07-a2.png`,
    badge:'A2 Gros cube',
    title:'Permis A2 — Yamaha MT-07',
    sub:  'Dès 18 ans · 20h · KTM 390 Duke dispo',
    priceLabel: 'à partir de',
    price: '1 290 €',
    priceNote: 'Prix public conseillé TTC',
    duration: '20h minimum',
    publicCible:
      "Candidats dès 18 ans visant le permis A2 (motos jusqu'à 35 kW). Passerelle A2→A possible après 2 ans.",
    keyPoints: [
      'Yamaha MT-07 bridée A2 (ou KTM 390 Duke)',
      '8h plateau + 12h circulation + débrief vidéo',
      'Préparation complète examen (plateau + ETM)',
      'Trajectoires de sécurité, freinage d\'urgence, évitement',
      'Passerelle A2 → A accessible après 2 ans (7h sans examen)',
    ],
    legalRef: 'Arrêté 23 avril 2012 modifié · Catégorie A2',
  },
  {
    code: 'pass125',
    img:  `${VAULT_BASE}/moto-honda-cb125-a1.png?v=3`,
    badge:'Formation 125',
    title:'Formation 125 (7h) — Honda CB125',
    sub:  'Permis B 2 ans min. · 7h · Sans examen',
    priceLabel: 'à partir de',
    price: '390 €',
    priceNote: 'Prix public conseillé TTC',
    duration: '7h (1 journée)',
    publicCible:
      "Titulaires du permis B depuis + de 2 ans souhaitant conduire légalement et en sécurité une motocyclette légère 125 cm³ ou un tricycle à moteur L5e (ex. scooter 3-roues). Public 35-55 ans en moyenne.",
    keyPoints: [
      '2h théorie : risques, équipements, règles de sécurité moto',
      '2h plateau : prise en main, équilibre, manœuvres à basse vitesse',
      '3h circulation : positionnement, vitesse, intersections, dépassements',
      'Aucun examen final — attestation remise le jour même',
      'Attestation à conserver et à présenter en cas de contrôle',
      'Mention 125cc portée au verso du permis B par la préfecture',
    ],
    dispenses: [
      "Permis B obtenu avant mars 1980",
      "Pratique attestée d'une 125 cm³ ou L5e entre 2006 et 2010, avec permis B depuis + de 2 ans",
    ],
    notes:
      "⚠ Important : la formation 125 (7h) permet uniquement de conduire une moto légère 125 cm³ ou un L5e avec le permis B. Elle ne délivre PAS le permis A1 et n'autorise pas la conduite d'une moto > 125 cm³.",
    priceOfficialRange: "Service-Public indique une fourchette officielle de 250 à 300 € selon les auto-écoles (tarifs libres).",
    legalRef: 'Arrêté du 7 janvier 2019 · Art. R221-8 du Code de la route',
  },
  {
    code: 'passA',
    img:  `${VAULT_BASE}/moto-ducati-a.png`,
    badge:'Passerelle A',
    title:'Passerelle A — Ducati',
    sub:  'Permis A2 2 ans min. · 7h · Sans examen',
    priceLabel: 'à partir de',
    price: '450 €',
    priceNote: 'Prix public conseillé TTC',
    duration: '7h (1 journée)',
    publicCible:
      "Titulaires du permis A2 depuis au moins 2 ans souhaitant accéder au permis A (motos > 35 kW, sans limite de puissance).",
    keyPoints: [
      '2h théorie : accidentologie, physique moto, équipement',
      '2h plateau : prise en main machine puissante',
      '3h circulation : conduite en situation réelle',
      'Aucun examen final — attestation remise à la fin',
      'Accès légal à toutes les motos > 35 kW',
    ],
    legalRef: 'Arrêté du 1er juin 2016 · Art. L221-2 CR',
  },
];

const MAILTO = 'mailto:contact@code2roues.fr?subject=Demande%20d%27acc%C3%A8s%20partenaire%20B2B%20Code2Roues&body=Bonjour%2C%0A%0AJ%27ai%20scann%C3%A9%20votre%20plaquette%20et%20je%20souhaite%20d%C3%A9couvrir%20votre%20offre%20B2B%20compl%C3%A8te.%0A%0ANom%20auto-%C3%A9cole%20%3A%0AAgr%C3%A9ment%20%3A%0AVille%20%3A%0A%0AMerci%20de%20me%20transmettre%20le%20lien%20d%27acc%C3%A8s%20partenaire.%0A%0ACordialement%2C';

export default function PlaquetteProspects() {
  // Formation sélectionnée pour la modal
  const [selected, setSelected] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="plaquette-prospects">
      {/* HERO */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white px-4 py-10 md:py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-400/30 rounded-full px-3 py-1 text-xs text-orange-300 tracking-widest uppercase mb-4">
            <Sparkles className="w-3 h-3" /> Partenariat B2B · Auto-écoles
          </div>
          <h1 className="text-3xl md:text-5xl font-bold leading-tight" style={{fontFamily:'Fraunces, serif'}}>
            Dites <span className="text-orange-400">OUI</span> aux clients AM, A1, A2, Formation 125 et Passerelle A<br />
            <span className="text-slate-300 text-xl md:text-3xl">sans investir un seul euro dans un véhicule moto</span>
          </h1>
          <p className="mt-5 text-slate-300 text-sm md:text-base max-w-2xl mx-auto">
            90% des auto-écoles refusent les candidats moto AM / A1 / A2 ainsi que les formations 7h faute de véhicule.
            Code2Roues met à votre disposition sa flotte complète (Citroën Ami, Honda CB125, Yamaha MT-07, KTM 390 Duke, Ducati) et son expertise ECSR + CCS Deux Roues.
          </p>

          <div className="mt-7 grid grid-cols-3 gap-3 max-w-xl mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-xs text-slate-400 uppercase tracking-wide">Gain</div>
              <div className="text-xl md:text-2xl font-bold text-orange-400 mt-1">5 200 €</div>
              <div className="text-[10px] text-slate-400">par mois</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-xs text-slate-400 uppercase tracking-wide">Investi.</div>
              <div className="text-xl md:text-2xl font-bold text-emerald-400 mt-1">0 €</div>
              <div className="text-[10px] text-slate-400">zéro véhicule</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-xs text-slate-400 uppercase tracking-wide">Délai</div>
              <div className="text-xl md:text-2xl font-bold text-white mt-1">24h</div>
              <div className="text-[10px] text-slate-400">activation</div>
            </div>
          </div>

          <a
            href={MAILTO}
            className="inline-flex items-center gap-2 mt-8 bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold text-sm md:text-base py-3 px-6 rounded-full shadow-lg transition-all hover:scale-[1.03]"
            data-testid="btn-request-access"
          >
            Demander l'accès partenaire complet <ArrowRight className="w-4 h-4" />
          </a>
          <p className="mt-3 text-[11px] text-slate-500">
            Vous recevez un lien privé vers notre tarification B2B détaillée sous 24h ouvrées.
          </p>
        </div>
      </section>

      {/* 5 PRESTATIONS */}
      <section className="max-w-6xl mx-auto px-4 py-10 md:py-14">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" style={{fontFamily:'Fraunces, serif'}}>
          5 prestations — tout le matériel inclus
        </h2>
        <p className="text-center text-sm text-slate-500 mb-3">Je viens avec mon véhicule, vous gardez votre client.</p>
        <p className="text-center text-[11px] text-orange-600 font-bold uppercase tracking-widest mb-8">
          👇 Cliquez sur une prestation pour un devis instantané
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FORMATIONS.map((f) => (
            <button
              type="button"
              key={f.code}
              onClick={() => setSelected(f)}
              className="group text-left bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-orange-400 hover:-translate-y-1 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              data-testid={`prestation-card-${f.code}`}
              aria-label={`Détails et devis ${f.title}`}
            >
              <div className="relative bg-white h-44 flex items-center justify-center p-4 border-b border-slate-100">
                <img src={f.img} alt={f.title} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                {/* Badge "Devis en 1 clic" en overlay */}
                <span className="absolute top-2 right-2 bg-orange-500 text-slate-900 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                  Devis instantané →
                </span>
              </div>
              <div className="p-4">
                <span className="inline-block bg-orange-500 text-slate-900 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full">
                  {f.badge}
                </span>
                <h3 className="text-lg font-semibold mt-2" style={{fontFamily:'Fraunces, serif'}}>{f.title}</h3>
                <p className="text-xs text-slate-500 mt-1">{f.sub}</p>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-end justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] text-slate-400 uppercase tracking-wide">{f.priceLabel}</div>
                    <div className="text-xl font-bold">{f.price}</div>
                    <div className="text-[10px] text-slate-500">{f.priceNote}</div>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1 bg-orange-50 border border-orange-200 text-orange-700 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full group-hover:bg-orange-500 group-hover:text-white group-hover:border-orange-500 transition-colors">
                    Détails <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Note passerelles */}
        <div className="mt-8 bg-gradient-to-r from-purple-900 to-indigo-900 text-white rounded-2xl p-5 md:p-6 shadow-md">
          <div className="flex items-start gap-3">
            <div className="bg-white/10 border border-white/20 rounded-xl p-2.5">
              <Zap className="w-5 h-5 text-amber-300" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg md:text-xl font-bold" style={{fontFamily:'Fraunces, serif'}}>
                Formation 125 & Passerelle A — 7h sans examen
              </h3>
              <p className="text-xs md:text-sm text-slate-200 mt-1">
                <b>Formation 125 (7h)</b> pour les titulaires du permis B depuis 2 ans min. (mention 125cc portée au verso du permis B — public 35-55 ans).
                <b> Passerelle A (7h)</b> pour les titulaires du permis A2 depuis 2 ans min. qui veulent accéder aux motos puissantes (&gt; 35 kW).
                Attestation remise le jour même, aucun examen présenté au centre.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ARGUMENTAIRE */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{fontFamily:'Fraunces, serif'}}>
            Pourquoi nous rejoindre ?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { Icon: TrendingUp, title:'Vous gardez votre client',
                body:'Inscription sous votre agrément, encaissement direct chez vous, marge nette garantie à chaque élève.' },
              { Icon: Zap, title:'Zéro investissement',
                body:'Pas d’achat ni d’entretien de moto. Je viens avec mon matériel au jour prévu, carburant / usure / assurance inclus.' },
              { Icon: Shield, title:'Image préservée',
                body:'L’épreuve de circulation est faite avec votre véhicule auto-école. Votre identité reste visible auprès de l’examinateur.' },
              { Icon: Users, title:'Clients + partenariat 7j/7',
                body:'Je m’adapte à vos créneaux et au rythme de vos élèves. Disponible 08h00-20h00, 7 jours sur 7.' },
              { Icon: CheckCircle2, title:'ECSR + CCS Deux Roues',
                body:'Aut. A 26 038 0007 0 Isère (valide 08/07/2032). RC Pro INCLUSE. Rattachement agrément Art. R212-3.' },
              { Icon: Sparkles, title:'TVA non applicable',
                body:'Art. 293 B CGI — Prix nets, sans surprise. Facturation à réception — IBAN FR76 2823 3000 015364087080 215.' },
            ].map(({Icon, title, body}) => (
              <div key={title} className="flex gap-3">
                <Icon className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">{title}</div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="max-w-3xl mx-auto px-4 py-10 md:py-14">
        <div className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-center" style={{fontFamily:'Fraunces, serif'}}>
            Prêt à rejoindre Code2Roues ?
          </h2>
          <p className="text-center text-sm text-slate-300 mt-2">
            Contactez-nous et recevez votre accès partenaire complet (tarifs B2B + marge détaillée)
          </p>

          <div className="mt-6 grid md:grid-cols-2 gap-3">
            <a href="tel:+33782268200" className="bg-white/5 border border-white/10 hover:bg-white/10 rounded-xl p-4 text-center transition" data-testid="btn-call">
              <Phone className="w-5 h-5 text-orange-400 mx-auto" />
              <div className="text-sm font-semibold mt-2">07 82 26 82 00</div>
              <div className="text-[11px] text-slate-400">7j/7 · 08h-20h</div>
            </a>
            <a href={MAILTO} className="bg-orange-500 hover:bg-orange-600 text-slate-900 rounded-xl p-4 text-center transition font-semibold" data-testid="btn-email">
              <Mail className="w-5 h-5 mx-auto" />
              <div className="text-sm font-bold mt-2">Demander l'accès</div>
              <div className="text-[11px] opacity-80">Réponse sous 24h</div>
            </a>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Saint-Savin 38300</span>
            <span>Nord Isère · Est Lyonnais · Drôme</span>
            <span>Déplacement INCLUS</span>
          </div>
        </div>

        <p className="text-center text-[10px] text-slate-400 mt-4">
          Llorens Bruno — Auto-Entrepreneur · ECSR + CCS Deux Roues · Aut. A 26 038 0007 0 Isère (Valide 08/07/2032) ·
          TVA non applicable Art. 293 B CGI · Code2Roues
        </p>
      </section>

      {/* ───── MODAL DÉTAIL + DEVIS/INFOS ───── */}
      <PrestationDialog
        prestation={selected ? toPrestation(selected) : null}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
